import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { api } from '../../utils/api';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Globe, 
  Smartphone, 
  ShieldCheck, 
  Package,
  Layers,
  Image as ImageIcon,
  CheckCircle2,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export const AdminApps = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentApp, setCurrentApp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    icon_url: '',
    cover_url: '',
    demo_url: '',
    featured: false,
    published: true,
    size: '',
    version: '1.0.0',
    price_type: 'free',
    price: '',
    os: [],
    permissions: [],
    gallery: []
  });

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const res = await api.apps.getAll();
      setApps(res.data || []);
    } catch (error) {
      toast.error('Error al cargar aplicaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (app = null) => {
    if (app) {
      setCurrentApp(app);
      setFormData({
        ...app,
        os: app.os || [],
        permissions: app.permissions || [],
        gallery: app.gallery || []
      });
    } else {
      setCurrentApp(null);
      setFormData({
        name: '',
        category: '',
        description: '',
        icon_url: '',
        cover_url: '',
        demo_url: '',
        featured: false,
        published: true,
        size: '',
        version: '1.0.0',
        price_type: 'free',
        price: '',
        os: [],
        permissions: [],
        gallery: []
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentApp) {
        await api.apps.update(currentApp.id, formData);
        toast.success('Aplicación actualizada');
      } else {
        await api.apps.create(formData);
        toast.success('Aplicación creada');
      }
      setIsModalOpen(false);
      fetchApps();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta aplicación?')) {
      try {
        await api.apps.delete(id);
        toast.success('Eliminada correctamente');
        fetchApps();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const filteredApps = apps.filter(app => 
    app.name.toLowerCase().includes(search.toLowerCase()) ||
    app.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-12">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="text-[#C6FF00] font-black uppercase tracking-[0.3em] text-[10px] block mb-2">Nexus Inventory</span>
            <h1 className="text-4xl font-black tracking-tighter uppercase italic">Gestión de Apps</h1>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-3 px-8 py-4 bg-[#C6FF00] text-black font-black uppercase tracking-widest text-[10px] rounded-2xl hover:scale-105 transition-all shadow-[0_0_30px_rgba(198,255,0,0.2)]"
          >
            <Plus size={16} />
            Nueva Aplicación
          </button>
        </header>

        <div className="relative">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20" size={20} />
          <input
            type="text"
            placeholder="BUSCAR EN EL NÚCLEO..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white/[0.02] border border-white/5 rounded-2xl py-6 pl-16 pr-6 text-white text-xs font-black uppercase tracking-[0.2em] focus:border-[#C6FF00]/40 outline-none transition-all"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredApps.map((app) => (
              <motion.div
                key={app.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="group p-8 rounded-[3rem] bg-white/[0.02] border border-white/5 hover:border-[#C6FF00]/20 transition-all relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-6 flex gap-2">
                  <button onClick={() => handleOpenModal(app)} className="p-3 bg-white/5 rounded-xl text-white/40 hover:text-[#C6FF00] hover:bg-[#C6FF00]/10 transition-all">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(app.id)} className="p-3 bg-white/5 rounded-xl text-white/40 hover:text-red-500 hover:bg-red-500/10 transition-all">
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="flex items-center gap-6 mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center p-2 group-hover:border-[#C6FF00]/40 transition-all">
                    <img src={app.icon_url || 'https://via.placeholder.com/64'} alt="" className="w-full h-full object-contain rounded-lg" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black uppercase tracking-tight text-white group-hover:text-[#C6FF00] transition-colors">{app.name}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-3 py-1 bg-white/5 rounded-full text-[8px] font-black uppercase tracking-widest text-white/40">{app.category}</span>
                      {app.featured && (
                        <span className="flex items-center gap-1 text-[#C6FF00] text-[8px] font-black uppercase tracking-widest">
                          <Zap size={8} fill="currentColor" />
                          Destacada
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                    <span className="text-white/20">Versión</span>
                    <span className="text-white/60">{app.version}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                    <span className="text-white/20">Precio</span>
                    <span className="text-[#C6FF00]">{app.price_type === 'free' ? 'GRATIS' : `${app.price || '0'} USD`}</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                    <span className="text-white/20">Estado</span>
                    <span className={app.published ? 'text-[#C6FF00]' : 'text-red-500/50'}>
                      {app.published ? 'PUBLICADO' : 'BORRADOR'}
                    </span>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-white/5 grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-[8px] font-black uppercase tracking-widest text-white/20">OS Suport</span>
                    <div className="flex gap-2">
                      {app.os?.map(o => <span key={o} className="text-white/40"><Globe size={12} /></span>)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 text-right">
                    <span className="text-[8px] font-black uppercase tracking-widest text-white/20">ID Núcleo</span>
                    <span className="text-white/10 text-[8px] font-mono truncate">#{app.id.slice(0,8)}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Modal App Rebuild */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              className="absolute inset-0 bg-black/95 backdrop-blur-xl" 
              onClick={() => setIsModalOpen(false)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/5 rounded-[3rem] p-12 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-3xl font-black uppercase italic tracking-tighter">
                  {currentApp ? 'Configurar App' : 'Nueva Secuencia de App'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-white/20 hover:text-white transition-colors">
                  <Trash2 size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-12">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Nombre de la App</label>
                    <input
                      required
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white outline-none focus:border-[#C6FF00]/40"
                    />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Categoría</label>
                    <select
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white outline-none focus:border-[#C6FF00]/40 appearance-none"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="Productivity">Productividad</option>
                      <option value="Security">Seguridad</option>
                      <option value="AI">Inteligencia Artificial</option>
                      <option value="Web3">Blockchain / Web3</option>
                      <option value="DevTools">Herramientas Dev</option>
                    </select>
                  </div>
                </div>

                {/* URLs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="space-y-4 md:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Icon URL</label>
                    <input
                      value={formData.icon_url}
                      onChange={e => setFormData({...formData, icon_url: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white outline-none focus:border-[#C6FF00]/40"
                    />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Versión</label>
                    <input
                      value={formData.version}
                      onChange={e => setFormData({...formData, version: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white outline-none focus:border-[#C6FF00]/40"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Descripción Detallada</label>
                  <textarea
                    rows={4}
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-3xl p-8 text-white outline-none focus:border-[#C6FF00]/40 resize-none"
                  />
                </div>

                {/* Toggles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, featured: !formData.featured})}
                    className={`p-6 rounded-3xl border transition-all flex flex-col items-center gap-4 ${
                      formData.featured ? 'bg-[#C6FF00]/10 border-[#C6FF00]/40 text-[#C6FF00]' : 'bg-white/5 border-white/5 text-white/40'
                    }`}
                  >
                    <Zap size={24} />
                    <span className="text-[8px] font-black uppercase tracking-[0.3em]">Destacada</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, published: !formData.published})}
                    className={`p-6 rounded-3xl border transition-all flex flex-col items-center gap-4 ${
                      formData.published ? 'bg-white/10 border-white/40 text-white' : 'bg-white/5 border-white/5 text-white/20'
                    }`}
                  >
                    {formData.published ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
                    <span className="text-[8px] font-black uppercase tracking-[0.3em]">Publicada</span>
                  </button>
                </div>

                {/* Additional Metadata */}
                <div className="space-y-8 pt-12 border-t border-white/5">
                  <h4 className="text-white font-black uppercase tracking-[0.4em] text-xs">Especificaciones Técnicas</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Sistemas Operativos</label>
                      <div className="flex flex-wrap gap-3">
                        {['Web', 'iOS', 'Android', 'Windows', 'MacOS', 'Browser Extension'].map(os => (
                          <button
                            key={os}
                            type="button"
                            onClick={() => toggleArrayItem('os', os)}
                            className={`px-4 py-2 rounded-xl border text-[10px] font-black uppercase transition-all ${
                              formData.os.includes(os) ? 'bg-[#C6FF00] text-black border-transparent' : 'bg-white/5 border-white/5 text-white/40'
                            }`}
                          >
                            {os}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Permisos Requeridos</label>
                      <div className="flex flex-wrap gap-3">
                        {['Auth', 'Database', 'Real-time', 'AI Models', 'Storage', 'Analytics'].map(p => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => toggleArrayItem('permissions', p)}
                            className={`px-4 py-2 rounded-xl border text-[10px] font-black uppercase transition-all ${
                              formData.permissions.includes(p) ? 'bg-white/20 border-white/40 text-white' : 'bg-white/5 border-white/5 text-white/20'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-6 pt-12">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-10 py-5 rounded-2xl border border-white/5 text-white/40 font-black uppercase tracking-widest text-[10px] hover:bg-white/5 transition-all"
                  >
                    Cancelar Operación
                  </button>
                  <button
                    type="submit"
                    className="px-12 py-5 bg-[#C6FF00] text-black rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-105 transition-all shadow-[0_0_50px_rgba(198,255,0,0.3)]"
                  >
                    Inyectar Datos
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
